import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useDragReorder } from "./useDragReorder";

const emptyForm = { name: "", hours: "", photos_count: "", videos_count: "", clips_count: "", video_master_count: "", description: "", price: "" };

/** All state/handlers for the Admin > Paquetes tab: CRUD, category filter, drag reorder. */
export const usePackageManager = () => {
  const [packages, setPackages] = useState([]);
  const [category, setCategory] = useState("bodas");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/packages").then((r) => setPackages(r.data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const categoryPackages = packages.filter((p) => p.category === category);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const changeCategory = (v) => {
    setCategory(v);
    resetForm();
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      name: pkg.name, hours: pkg.hours, photos_count: pkg.photos_count || "", videos_count: pkg.videos_count || "",
      clips_count: pkg.clips_count || "", video_master_count: pkg.video_master_count || "",
      description: pkg.description, price: pkg.price,
    });
  };

  const submit = async () => {
    const requiresHours = category !== "invitacion_digital";
    if (!form.name.trim() || (requiresHours && !form.hours) || !form.price) {
      toast.error(`Completa nombre${requiresHours ? ", horas" : ""} y precio`);
      return;
    }
    if (
      Number(form.hours || 0) < 0 || Number(form.price) < 0 || Number(form.photos_count || 0) < 0 ||
      Number(form.videos_count || 0) < 0 || Number(form.clips_count || 0) < 0 || Number(form.video_master_count || 0) < 0
    ) {
      toast.error("Ningún número puede ser negativo");
      return;
    }
    setSaving(true);
    const payload = {
      category, name: form.name, hours: requiresHours ? Number(form.hours) : 0, photos_count: Number(form.photos_count) || 0,
      videos_count: Number(form.videos_count) || 0,
      clips_count: Number(form.clips_count) || 0, video_master_count: Number(form.video_master_count) || 0,
      description: form.description, price: Number(form.price),
    };
    try {
      if (editingId) {
        await api.put(`/admin/packages/${editingId}`, payload);
        toast.success("Paquete actualizado");
      } else {
        await api.post("/admin/packages", payload);
        toast.success("Paquete creado");
      }
      resetForm();
      await load();
    } catch {
      toast.error("Error al guardar el paquete");
    } finally {
      setSaving(false);
    }
  };

  const removePackage = async (id) => {
    try {
      await api.delete(`/admin/packages/${id}`);
      toast.success("Paquete eliminado");
      await load();
    } catch {
      toast.error("Error al eliminar el paquete");
    }
  };

  const dragReorder = useDragReorder({
    filteredItems: categoryPackages,
    setAllItems: setPackages,
    filterKey: "category",
    filterValue: category,
    reorderRequest: (orderedIds) => api.put("/admin/packages/reorder", { category, ordered_ids: orderedIds }),
    reload: load,
  });

  return {
    category, changeCategory, categoryPackages, form, setForm, editingId, saving,
    startEdit, submit, removePackage, resetForm, dragReorder,
  };
};
